import { useState, forwardRef, useImperativeHandle } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Box,
  Typography,
  FormLabel,
  TextField,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import httpclient from "../../../Utils";
import { useNotification } from "../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../Hooks/useApiErrorHandler";

export const CancelOrderAction = forwardRef(({ onCancel, jobId }, ref) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reasonText, setReasonText] = useState("");
  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  const handleOpenDialog = () => setOpen(true);

  const handleCloseDialog = () => {
    setOpen(false);
    setReasonText("");
  };

  useImperativeHandle(ref, () => ({
    openDialog: handleOpenDialog,
    closeDialog: handleCloseDialog,
  }));

  const handleConfirm = async () => {
    if (reasonText.length < 5) {
      showNotification("error", "Reason must be at least 5 characters");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("canceled_reason", reasonText);

      const endpoint = `/orders/${jobId}/cancel-order`;
      const res = await httpclient.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const { data } = res;
      if (data.status === 200) {
        showNotification(
          "success",
          data.message || "Order cancelled successfully"
        );
        onCancel();
        handleCloseDialog();
      } else {
        showNotification("error", data.message || "Failed to cancel order");
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Cancel Order
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Are you sure you want to cancel this entire order? This action
            cannot be undone.
          </DialogContentText>

          <FormControl fullWidth>
            <FormLabel sx={{ mb: 2, color: "text.primary" }}>
              Please enter a reason for canceling the order:
            </FormLabel>
            <TextField
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              fullWidth
              inputProps={{ maxLength: 50 }}
              placeholder="Enter reason (5-50 characters)"
            />
            <Typography
              variant="caption"
              color={
                reasonText.length < 5 || reasonText.length > 50
                  ? "error"
                  : "textSecondary"
              }
              sx={{ mt: 1, display: "block", textAlign: "right" }}
            >
              {reasonText.length}/50 characters
              {reasonText.length < 5 && " (minimum 5 characters required)"}
            </Typography>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseDialog}
            disabled={isSubmitting}
            variant="outlined"
          >
            No, Keep Order
          </Button>
          <Button
            onClick={handleConfirm}
            color="error"
            variant="contained"
            disabled={
              reasonText.length < 5 || reasonText.length > 50 || isSubmitting
            }
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Yes, Cancel Order"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export const HoldOrderAction = forwardRef(({ onHold, jobId }, ref) => {
  const [open, setOpen] = useState(false);
  const [holdReasons, setHoldReasons] = useState([]);
  const [selectedReasonId, setSelectedReasonId] = useState("");
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  const fetchHoldReasons = async () => {
    setReasonsLoading(true);
    try {
      const res = await httpclient.get("/order-hold-reason-list");
      const { data } = res;
      if (data.status === 200) {
        setHoldReasons(data.data || []);
      } else {
        showNotification(
          "error",
          data.message || "Failed to fetch hold reasons"
        );
        setHoldReasons([]);
      }
    } catch (error) {
      handleApiError(error);
      setHoldReasons([]);
    } finally {
      setReasonsLoading(false);
    }
  };

  const openDialogAndFetchReasons = () => {
    setOpen(true);
    setSelectedReasonId(""); // Reset selected reason
    fetchHoldReasons();
  };
  const handleCloseDialog = () => setOpen(false);

  useImperativeHandle(ref, () => ({
    openDialog: openDialogAndFetchReasons,
    closeDialog: handleCloseDialog,
  }));

  const handleConfirm = async () => {
    if (!selectedReasonId) {
      showNotification("error", "Please select a reason to hold the order.");
      return;
    }
    setIsSubmitting(true);
    try {
      const endpoint = `/orders/${jobId}/hold-order`;
      const formData = new FormData();
      formData.append("reason_id", selectedReasonId);
      const res = await httpclient.post(endpoint, formData);
      const { data } = res;

      if (data.status === 200) {
        onHold(); // Callback to refresh data or close modal
        handleCloseDialog();
        showNotification(
          "success",
          data.message || "Order placed on hold successfully"
        );
      } else {
        showNotification("error", data.message || "Failed to hold order");
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Dialog only, trigger is handled by parent */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Hold Order</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to put this order on hold? Please select a
            reason below.
          </DialogContentText>
          {reasonsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <FormControl
              fullWidth
              margin="normal"
              disabled={isSubmitting}
              sx={{ my: 2 }}
            >
              <InputLabel id="hold-reason-label">Reason for Hold</InputLabel>
              <Select
                labelId="hold-reason-label"
                id="hold-reason-select"
                value={selectedReasonId}
                label="Reason for Hold"
                onChange={(e) => setSelectedReasonId(e.target.value)}
              >
                <MenuItem value="">
                  <em>Select a reason</em>
                </MenuItem>
                {holdReasons.map((reason) => (
                  <MenuItem key={reason.id} value={reason.id}>
                    {reason.reason_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSubmitting}>
            No
          </Button>
          <Button
            onClick={handleConfirm}
            color="warning"
            variant="contained"
            disabled={reasonsLoading || !selectedReasonId || isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Yes, Hold Order"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export const CancelLineButton = ({ lineId, onCancel }) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reasonText, setReasonText] = useState("");
  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  const handleOpenDialog = () => setOpen(true);

  const handleCloseDialog = () => {
    setOpen(false);
    setReasonText("");
  };

  const handleConfirm = async () => {
    if (reasonText.length < 5) {
      showNotification("error", "Reason must be at least 5 characters");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("canceled_reason", reasonText);

      const endpoint = `/orders/${lineId}/cancel-order-item`;
      const res = await httpclient.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const { data } = res;
      if (data.status === 200) {
        showNotification(
          "success",
          data.message || "Order line cancelled successfully"
        );
        onCancel();
        handleCloseDialog();
      } else {
        showNotification(
          "error",
          data.message || "Failed to cancel order line"
        );
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Tooltip title="Cancel Line">
        <IconButton size="small" onClick={handleOpenDialog} color="error">
          <CancelIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Cancel Order Line
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Are you sure you want to cancel this order line? This action cannot
            be undone.
          </DialogContentText>

          <FormControl fullWidth>
            <FormLabel sx={{ mb: 2, color: "text.primary" }}>
              Please enter a reason for canceling the order line:
            </FormLabel>
            <TextField
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              fullWidth
              inputProps={{ maxLength: 50 }}
              placeholder="Enter reason (5-50 characters)"
            />
            <Typography
              variant="caption"
              color={
                reasonText.length < 5 || reasonText.length > 50
                  ? "error"
                  : "textSecondary"
              }
              sx={{ mt: 1, display: "block", textAlign: "right" }}
            >
              {reasonText.length}/50 characters
              {reasonText.length < 5 && " (minimum 5 characters required)"}
            </Typography>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseDialog}
            disabled={isSubmitting}
            variant="outlined"
          >
            No, Keep Line
          </Button>
          <Button
            onClick={handleConfirm}
            color="error"
            variant="contained"
            disabled={
              reasonText.length < 5 || reasonText.length > 50 || isSubmitting
            }
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Yes, Cancel Line"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const ReleaseOrderAction = forwardRef(({ onRelease, jobId }, ref) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  const handleOpenDialog = () => setOpen(true);
  const handleCloseDialog = () => setOpen(false);

  useImperativeHandle(ref, () => ({
    openDialog: handleOpenDialog,
    closeDialog: handleCloseDialog,
  }));

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const endpoint = `/orders/${jobId}/release-order`;
      const res = await httpclient.post(endpoint);
      const { data } = res;

      if (data.status === 200) {
        showNotification(
          "success",
          data.message || "Order released successfully"
        );
        onRelease();
        handleCloseDialog();
      } else {
        showNotification("error", data.message || "Failed to release order");
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Dialog only, trigger is handled by parent */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Release Order</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to release this order? This will make it
            available for picking.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSubmitting}>
            No, Keep
          </Button>
          <Button
            onClick={handleConfirm}
            color="success"
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Yes, Release Order"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export const AssignToPickerPackerAction = forwardRef(
  ({ onAssign, jobId }, ref) => {
    const [open, setOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const showNotification = useNotification();
    const handleApiError = useApiErrorHandler();

    const handleOpenDialog = () => setOpen(true);
    const handleCloseDialog = () => setOpen(false);

    useImperativeHandle(ref, () => ({
      openDialog: handleOpenDialog,
      closeDialog: handleCloseDialog,
    }));

    const handleConfirm = async () => {
      setIsSubmitting(true);
      try {
        const endpoint = `/orders/${jobId}/release-order`;
        const formData = new FormData();
        formData.append("released_by_picker_packer", 1);
        const res = await httpclient.post(endpoint, formData);
        const { data } = res;

        if (data.status === 200) {
          showNotification(
            "success",
            data.message ||
              "Order assigned to original picker/packer successfully"
          );
          onAssign();
          handleCloseDialog();
        } else {
          showNotification("error", data.message || "Failed to assign order");
        }
      } catch (error) {
        handleApiError(error);
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <>
        {/* Dialog only, trigger is handled by parent */}
        <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Assign to Same Picker/Packer</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to release this order and assign it back to
              the original picker/packer?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={isSubmitting}>
              No
            </Button>
            <Button
              onClick={handleConfirm}
              color="primary"
              variant="contained"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Yes, Assign"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export const CompleteOrderAction = forwardRef(({ onComplete, jobId }, ref) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  const handleOpenDialog = () => setOpen(true);

  const handleCloseDialog = () => {
    setOpen(false);
    setInvoiceNumber("");
  };

  useImperativeHandle(ref, () => ({
    openDialog: handleOpenDialog,
    closeDialog: handleCloseDialog,
  }));

  const handleConfirm = async () => {
    if (!invoiceNumber.trim()) {
      showNotification("error", "Invoice number is required");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("invoice_number", invoiceNumber.trim());

      const endpoint = `/orders/${jobId}/complete-order`;
      const res = await httpclient.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const { data } = res;
      if (data.status === 200) {
        showNotification(
          "success",
          data.message || "Order marked as completed successfully"
        );
        onComplete();
        handleCloseDialog();
      } else {
        showNotification("error", data.message || "Failed to complete order");
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          Mark as Completed
        </Typography>
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 3 }}>
          This order has been picked and ready to be packed. Are you sure you
          want to mark this order as completed?
        </DialogContentText>

        <FormControl fullWidth>
          <FormLabel sx={{ mb: 2, color: "text.primary" }}>
            Please enter the invoice number:
          </FormLabel>
          <TextField
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            fullWidth
            placeholder="Enter invoice number"
          />
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleCloseDialog}
          disabled={isSubmitting}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={!invoiceNumber.trim() || isSubmitting}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Yes, Mark as Completed"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export const RefetchFromERPAction = forwardRef(({ onRefetch, jobId }, ref) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  const handleOpenDialog = () => setOpen(true);
  const handleCloseDialog = () => setOpen(false);

  useImperativeHandle(ref, () => ({
    openDialog: handleOpenDialog,
    closeDialog: handleCloseDialog,
  }));

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const endpoint = `/orders/${jobId}/refetch-order`;
      const res = await httpclient.get(endpoint);
      const { data } = res;

      if (data.status === 200) {
        showNotification(
          "success",
          data.message || "Order refetched from ERP successfully"
        );
        onRefetch();
        handleCloseDialog();
      } else {
        showNotification(
          "error",
          data.message || "Failed to refetch order from ERP"
        );
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Refetch Order from ERP</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to refetch this order from ERP?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            disabled={isSubmitting}
            variant="outlined"
          >
            No, Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Yes, Refetch Order"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});
